/**
 * Layer 4: Threat Capsule Network (TCN) — POLISHED
 * Armadillo Adamantium Security Framework
 * 
 * Responsibility: Create, verify, and query threat capsules.
 * Threat capsules are signed containers of attack data that can be shared
 * across nodes (peer-to-peer threat intelligence).
 * 
 * Polish additions:
 * - Signed gossip protocol with hop count limits
 * - Deduplication via content hashing
 * - Confidence decay over time
 * - Conflict resolution (supersede chain)
 * - Rapid revocation propagation
 */

import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import { sha256 } from 'js-sha256';
import { ThreatCapsule, GossipMessage } from './types';

// ============================================
// CAPSULE CREATION
// ============================================

/**
 * Create a new threat capsule
 * Signs the capsule with Ed25519 for integrity verification
 */
export async function createCapsule(
  attackData: {
    type: ThreatCapsule['type'];
    ipHash: string;
    signals: Record<string, any>;
    confidence: number;
  }
): Promise<ThreatCapsule> {
  // Generate unique capsule ID
  const capsuleId = crypto.randomUUID();
  
  // Set expiry (30 days default)
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  
  // Build unsigned capsule
  const unsignedCapsule: Omit<ThreatCapsule, 'signature'> = {
    capsuleId,
    type: attackData.type,
    signals: attackData.signals,
    confidence: Math.min(1, Math.max(0, attackData.confidence)),
    reputation: 0.5, // Default reputation
    expiresAt,
    createdAt: new Date(),
    ipPrefix: attackData.ipHash.slice(0, 6)
  };
  
  // Sign capsule
  const signature = await signCapsule(unsignedCapsule);
  
  const capsule: ThreatCapsule = {
    ...unsignedCapsule,
    signature
  };
  
  // Store in local cache
  await cacheCapsule(capsule);
  
  // Optional: Gossip to peers (future implementation)
  // await gossipToPeers(capsule);
  
  return capsule;
}

/**
 * Sign a threat capsule with Ed25519
 */
async function signCapsule(capsule: Omit<ThreatCapsule, 'signature'>): Promise<string> {
  // Serialize capsule (excluding signature field)
  const message = JSON.stringify({
    capsuleId: capsule.capsuleId,
    type: capsule.type,
    signals: capsule.signals,
    confidence: capsule.confidence,
    expiresAt: capsule.expiresAt.toISOString(),
    createdAt: capsule.createdAt.toISOString(),
    ipPrefix: capsule.ipPrefix
  });
  
  // Get or generate signing key
  const keyPair = getSigningKeyPair();
  
  // Sign
  const signature = nacl.sign.detached(
    new TextEncoder().encode(message),
    keyPair.secretKey
  );
  
  return encodeBase64(signature);
}

/**
 * Get or generate signing key pair for capsules
 */
function getSigningKeyPair(): { publicKey: Uint8Array; secretKey: Uint8Array } {
  const publicKeyEnv = (typeof process !== 'undefined' && (process as any).env ? (process as any).env.ARMADILLO_CAPSULE_PUBLIC_KEY : null);
  const privateKeyEnv = (typeof process !== 'undefined' && (process as any).env ? (process as any).env.ARMADILLO_CAPSULE_PRIVATE_KEY : null);
  
  if (publicKeyEnv && privateKeyEnv) {
    return {
      publicKey: decodeBase64(publicKeyEnv),
      secretKey: decodeBase64(privateKeyEnv)
    };
  }
  
  // Generate new keys (development mode)
  const keyPair = nacl.sign.keyPair();

  
  return {
    publicKey: keyPair.publicKey,
    secretKey: keyPair.secretKey
  };
}

// ============================================
// CAPSULE VERIFICATION
// ============================================

/**
 * Verify a threat capsule signature
 * Returns true if capsule is authentic and unmodified
 */
export async function verifyCapsule(capsule: ThreatCapsule): Promise<boolean> {
  try {
    // Reconstruct the signed message
    const message = JSON.stringify({
      capsuleId: capsule.capsuleId,
      type: capsule.type,
      signals: capsule.signals,
      confidence: capsule.confidence,
      expiresAt: capsule.expiresAt.toISOString(),
      createdAt: capsule.createdAt.toISOString(),
      ipPrefix: capsule.ipPrefix
    });
    
    // Verify signature
    const { publicKey } = getSigningKeyPair();
    const isValid = nacl.sign.detached.verify(
      new TextEncoder().encode(message),
      decodeBase64(capsule.signature),
      publicKey
    );
    
    return isValid;
  } catch {
    return false;
  }
}

/**
 * Check if a threat capsule is still valid (not expired)
 */
export function isCapsuleValid(capsule: ThreatCapsule): boolean {
  return new Date() < new Date(capsule.expiresAt);
}

// ============================================
// CAPSULE QUERY
// ============================================

/**
 * Query threat capsules by IP prefix
 * Returns matching capsules with confidence > threshold
 */
export async function queryThreatCapsules(
  ipHash: string,
  confidenceThreshold: number = 0.7
): Promise<ThreatCapsule[]> {
  const ipPrefix = ipHash.slice(0, 6);
  const cacheKey = `armadillo_capsule:${ipPrefix}`;
  
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return [];
    
    const capsules: ThreatCapsule[] = JSON.parse(cached);
    
    // Filter valid capsules with high confidence
    return capsules.filter(capsule => 
      isCapsuleValid(capsule) && 
      capsule.confidence >= confidenceThreshold
    );
  } catch {
    return [];
  }
}

/**
 * Query threat capsules by JA4 fingerprint prefix
 * For browser fingerprint-based threat sharing
 */
export async function queryByFingerprint(
  fingerprintHash: string,
  confidenceThreshold: number = 0.7
): Promise<ThreatCapsule[]> {
  const fpPrefix = fingerprintHash.slice(0, 6);
  const cacheKey = `armadillo_capsule_fp:${fpPrefix}`;
  
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return [];
    
    const capsules: ThreatCapsule[] = JSON.parse(cached);
    
    return capsules.filter(capsule => 
      isCapsuleValid(capsule) && 
      capsule.confidence >= confidenceThreshold
    );
  } catch {
    return [];
  }
}

// ============================================
// CAPSULE CACHE MANAGEMENT
// ============================================

/**
 * Cache a threat capsule locally
 */
async function cacheCapsule(capsule: ThreatCapsule): Promise<void> {
  try {
    const ipPrefix = capsule.ipPrefix || 'unknown';
    const cacheKey = `armadillo_capsule:${ipPrefix}`;
    
    const cached = localStorage.getItem(cacheKey);
    let capsules: ThreatCapsule[] = cached ? JSON.parse(cached) : [];
    
    // Add new capsule
    capsules.push(capsule);
    
    // Remove expired capsules
    capsules = capsules.filter(isCapsuleValid);
    
    // Limit cache size (keep last 100)
    if (capsules.length > 100) {
      capsules = capsules.slice(-100);
    }
    
    localStorage.setItem(cacheKey, JSON.stringify(capsules));
  } catch (error) {

  }
}

/**
 * Clear expired capsules from cache
 */
export function cleanupExpiredCapsules(): void {
  const keys = Object.keys(localStorage).filter(k => k.startsWith('armadillo_capsule:'));
  
  keys.forEach(key => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return;
      
      const capsules: ThreatCapsule[] = JSON.parse(cached);
      const valid = capsules.filter(isCapsuleValid);
      
      if (valid.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(valid));
      }
    } catch {
      // Invalid data, remove
      localStorage.removeItem(key);
    }
  });
}

// ============================================
// PEER GOSSIP (POLISHED: SIGNED + DEDUP)
// ============================================

/**
 * Maximum hop count for gossip propagation
 * Prevents infinite loops and stale data
 */
const MAX_GOSSIP_HOPS = 3;
const GOSSIP_BATCH_INTERVAL_MS = 5000;

/**
 * Deduplication cache — prevents processing the same capsule twice
 */
const processedCapsuleHashes = new Set<string>();
const MAX_DEDUP_SIZE = 10000;

function computeDeduplicationHash(capsule: ThreatCapsule): string {
  return `${capsule.capsuleId}:${capsule.type}:${capsule.ipPrefix || ''}`;
}

function isDuplicate(capsule: ThreatCapsule): boolean {
  const hash = computeDeduplicationHash(capsule);
  if (processedCapsuleHashes.has(hash)) return true;
  processedCapsuleHashes.add(hash);
  if (processedCapsuleHashes.size > MAX_DEDUP_SIZE) {
    const arr = Array.from(processedCapsuleHashes);
    processedCapsuleHashes.clear();
    arr.slice(-5000).forEach(h => processedCapsuleHashes.add(h));
  }
  return false;
}

/**
 * Gossip capsule to peer nodes with signed envelope
 */
export async function gossipToPeers(capsule: ThreatCapsule): Promise<void> {
  const peers = (typeof process !== 'undefined' && (process as any).env ? (process as any).env.TCN_PEERS : null)?.split(',') || [];
  
  // Build signed gossip message
  const gossipMsg: GossipMessage = {
    capsuleId: capsule.capsuleId,
    originNodeId: capsule.originNodeId || 'local',
    hopCount: (capsule.gossipHopCount || 0) + 1,
    signature: capsule.signature,
    timestamp: Date.now(),
    deduplicationHash: computeDeduplicationHash(capsule),
  };
  
  // Don't propagate if hop count exceeded
  if (gossipMsg.hopCount > MAX_GOSSIP_HOPS) return;
  
  const body = JSON.stringify({ capsule, gossip: gossipMsg });
  
  for (const peerUrl of peers) {
    try {
      await fetch(`${peerUrl}/api/threat-capsules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Gossip-Hops': gossipMsg.hopCount.toString(),
          'X-Gossip-Signature': capsule.signature.slice(0, 32),
        },
        body,
      });
    } catch (_error) {
      // Peer unreachable — continue with others
    }
  }
}

/**
 * Receive capsule from peer with full validation
 */
export async function receiveCapsuleFromPeer(capsule: ThreatCapsule, gossip?: GossipMessage): Promise<boolean> {
  // 1. Check hop count
  if (gossip && gossip.hopCount > MAX_GOSSIP_HOPS) return false;
  
  // 2. Check deduplication
  if (isDuplicate(capsule)) return false;
  
  // 3. Verify signature
  const isValid = await verifyCapsule(capsule);
  if (!isValid) return false;
  
  // 4. Verify not expired
  if (!isCapsuleValid(capsule)) return false;
  
  // 5. Check conflict resolution (does this supersede an existing capsule?)
  if (capsule.supersedes) {
    // Remove the superseded capsule from cache
    removeSupersededCapsule(capsule.supersedes);
  }
  
  // 6. Apply confidence decay based on hop count
  const hopDecay = gossip ? Math.pow(0.9, gossip.hopCount) : 1.0;
  const adjustedCapsule = {
    ...capsule,
    confidence: capsule.confidence * hopDecay,
    gossipHopCount: gossip?.hopCount || 0,
  };
  
  // 7. Cache locally
  await cacheCapsule(adjustedCapsule);
  
  // 8. Re-gossip to our peers (if hop count allows)
  if (gossip && gossip.hopCount < MAX_GOSSIP_HOPS) {
    // Fire-and-forget
    gossipToPeers(adjustedCapsule).catch(() => {});
  }
  
  return true;
}

/**
 * Remove a superseded capsule from the cache
 */
function removeSupersededCapsule(capsuleId: string): void {
  try {
    const cacheKey = `armadillo_capsule:${capsuleId.slice(0, 8)}`;
    localStorage.removeItem(cacheKey);
  } catch {
    // No-op in non-browser environment
  }
}

// ============================================
// POLISH: CONFIDENCE DECAY
// ============================================

/**
 * Apply time-based confidence decay to a capsule
 * Capsules become less confident as they age
 */
export function applyConfidenceDecay(capsule: ThreatCapsule): number {
  const age = Date.now() - new Date(capsule.createdAt).getTime();
  const maxAge = new Date(capsule.expiresAt).getTime() - new Date(capsule.createdAt).getTime();
  if (maxAge <= 0) return 0;
  
  // Linear decay: confidence decreases as capsule ages
  const ageRatio = Math.min(1, age / maxAge);
  return capsule.confidence * (1 - ageRatio * 0.5); // At max age, confidence is halved
}

/**
 * Batch confidence decay for all cached capsules
 * Call periodically to clean up stale data
 */
export function batchConfidenceDecay(): number {
  let cleaned = 0;
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('armadillo_capsule:'));
    for (const key of keys) {
      const data = localStorage.getItem(key);
      if (!data) continue;
      const capsule: ThreatCapsule = JSON.parse(data);
      const decayedConfidence = applyConfidenceDecay(capsule);
      if (decayedConfidence < 0.05 || !isCapsuleValid(capsule)) {
        localStorage.removeItem(key);
        cleaned++;
      }
    }
  } catch {
    // No-op
  }
  return cleaned;
}

// ============================================
// POLISH: RAPID REVOCATION
// ============================================

/**
 * Issue a revocation capsule that invalidates a previous capsule
 */
export async function revokeCapsule(
  capsuleId: string,
  reason: string
): Promise<ThreatCapsule | null> {
  const revocationCapsule = await createCapsule({
    type: 'bot', // Using 'bot' type as revocation marker
    ipHash: 'revocation',
    signals: { revocation: true, targetCapsuleId: capsuleId, reason },
    confidence: 1.0,
  });
  
  if (revocationCapsule) {
    revocationCapsule.capsuleId = `revoke-${capsuleId}`;
    // Propagate revocation to peers
    await gossipToPeers(revocationCapsule);
  }
  
  return revocationCapsule;
}
